import useIsMobile from 'hooks/useIsMobile';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import { FaFileMedical, FaReceipt, FaUser, FaUserEdit } from 'react-icons/fa';
import { IoDocumentTextSharp } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import { atualizarAgenda } from 'services/agenda';
import { useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';
import { resolveUsuarioAgenda } from 'utils/agenda';
import { getApiErrorMessage } from 'utils/apiErrors';
import { navigateToProntuario } from '../../utils/prontuarioNav';
import { getStatusColor } from '../../utils/statusColors';
import PainelDoMedico from '../painel/PainelDoMedico';
import FaturamentoModal from './FaturamentoModal';
import PopupMenu from './PopupMenu';

const PALETTE_CONFIRMA = {
  confirmada: { backgroundColor: '#03fe31ff', color: '#00000fff', fontWeight: 'bold' },
  cancelar: { backgroundColor: '#dc3545', fontWeight: 'bold', color: '#fff' },
  reagendar: { backgroundColor: '#ff07d2ff', fontWeight: 'bold', color: '#fff' },
  atendente: { backgroundColor: '#2904fdff', fontWeight: 'bold', color: '#fff' },
};

const DEFAULT_CONFIRMA = { backgroundColor: '#6c757d', color: '#fff' };

const getConfirmaStyle = (statusConfirma) => {
  const status = statusConfirma ? String(statusConfirma).toLowerCase() : '';
  return PALETTE_CONFIRMA[status] || DEFAULT_CONFIRMA;
};

const AgendaRow = memo(
  ({
    agenda,
    index,
    isSelecionada,
    onSelectAgenda,
    onOpenAgenda,
    onRowKeyDown,
    setRowRef,
    verCanceladas,
    onToggleCanceladas,
    mostrarAtvStatus,
  }) => {
    const statusStyle = getStatusColor(agenda?.SITUACAO);
    const rowKey = String(agenda?.CODAGENDA);

    return (
      <tr
        ref={(el) => setRowRef?.(rowKey, el)}
        tabIndex={isSelecionada ? 0 : -1}
        aria-selected={isSelecionada}
        onClick={(e) => {
          onSelectAgenda?.(agenda);
          onOpenAgenda?.(agenda);
          e.currentTarget.focus({ preventScroll: true });
        }}
        onKeyDown={(e) => onRowKeyDown?.(e, index)}
        style={{
          ...statusStyle,
          cursor: 'pointer',
          outline: isSelecionada ? '2px solid #0d6efd' : 'none',
          ...(isSelecionada && {
            outlineOffset: '-2px',
            boxShadow: 'inset 0 0 0 9999px rgba(13, 110, 253, 0.15)',
          }),
        }}
      >
        <td
          className="text-nowrap"
          style={{
            borderLeft: isSelecionada ? '1px solid #0d6efd' : '1px solid transparent',
            fontWeight: 'bold',
          }}
        >
          {agenda.hora}
        </td>
        <td className="text-truncate" style={{ maxWidth: '160px' }} title={agenda.NOME}>
          {agenda.NOME}
        </td>
        <td className="text-truncate" style={{ maxWidth: '120px' }} title={agenda.PROCEDIMENTO}>
          {agenda.PROCEDIMENTO}
        </td>
        <td className="text-center">{agenda.ECG}</td>
        <td className="text-center text-nowrap">{agenda.CONVENIO}</td>
        {mostrarAtvStatus && (
          <td
            className="text-center text-truncate"
            style={{ maxWidth: '90px' }}
            title={agenda.ATIVIDADE}
          >
            {agenda.ATIVIDADE}
          </td>
        )}
        {mostrarAtvStatus && (
          <td className="text-center">
            <span
              className="d-inline-block px-1 rounded"
              style={{
                ...statusStyle,
                ...(statusStyle.backgroundColor && { border: '1px solid rgba(0,0,0,0.1)' }),
              }}
            >
              {agenda.SITUACAO}
            </span>
          </td>
        )}
        <td className="text-center">
          <Badge
            bg={null}
            style={{ ...getConfirmaStyle(agenda.STATUS_CONFIRMA) }}
            className="px-1 py-1"
          >
            {agenda.STATUS_CONFIRMA}
          </Badge>
        </td>
        <td style={{ maxWidth: '250px' }} title={agenda.OBSERVACAO}>
          {agenda.OBSERVACAO}
        </td>
        <td className="text-center py-0" onClick={(e) => e.stopPropagation()}>
          <PopupMenu
            agenda={agenda}
            onOpenAgenda={onOpenAgenda}
            verCanceladas={verCanceladas}
            onToggleCanceladas={onToggleCanceladas}
          />
        </td>
      </tr>
    );
  }
);

export default function AgendasTable({
  agendas = [],
  agendaSelecionada,
  onSelectAgenda,
  callStates = {},
  onCallPatient,
  onRecallPatient,
  onCancelPatient,
  onArrivePatient,
  onDonePatient,
  onViewCallHistory,
  onOpenAgenda,
  verCanceladas,
  onToggleCanceladas,
}) {
  const history = useHistory();
  const isMobile = useIsMobile();
  const func = useSelector(selectFunc);
  const doctor = useSelector(selectDoctor);
  const mostrarAtvStatus = Boolean(func) || !doctor;
  const colunasVisiveis = mostrarAtvStatus ? 10 : 8;
  const { mutate } = useSWRConfig();
  const { codFunc, nome: nomeFunc } = resolveUsuarioAgenda({ func, doctor });
  const [showFaturamentoModal, setShowFaturamentoModal] = useState(false);
  const rowRefs = useRef({});

  const setRowRef = useCallback((rowKey, el) => {
    if (el) {
      rowRefs.current[rowKey] = el;
    } else {
      delete rowRefs.current[rowKey];
    }
  }, []);

  const handleRowKeyDown = useCallback(
    (e, index) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.target.closest('.dropdown-menu, button, input, select, textarea, a')) return;

      e.preventDefault();
      const nextIndex =
        e.key === 'ArrowDown' ? Math.min(index + 1, agendas.length - 1) : Math.max(index - 1, 0);

      if (nextIndex === index) return;
      const nextAgenda = agendas[nextIndex];
      if (!nextAgenda) return;

      onSelectAgenda?.(nextAgenda);
      onOpenAgenda?.(nextAgenda);

      const nextKey = String(nextAgenda.CODAGENDA);
      rowRefs.current[nextKey]?.focus({ preventScroll: true });
      rowRefs.current[nextKey]?.scrollIntoView({ block: 'nearest' });
    },
    [agendas, onSelectAgenda, onOpenAgenda]
  );

  const atualizarTabelaAgenda = () =>
    mutate((key) => Array.isArray(key) && key[0] === '/medico/agendas');

  const { trigger: triggerAtualizarAgenda } = useSWRMutation('/agenda', atualizarAgenda, {
    onSuccess: atualizarTabelaAgenda,
    onError: (err) => toast.error(getApiErrorMessage(err, 'Erro ao atualizar agenda')),
    throwOnError: false,
  });

  const atualizarSituacaoFaturado = () => {
    if (!agendaSelecionada) return;
    triggerAtualizarAgenda({
      codAgenda: agendaSelecionada.CODAGENDA,
      ...(codFunc ? { codFuncMarcou: codFunc } : {}),
      evolucao: `${nomeFunc} - Faturado`,
    });
  };

  // Auto-seleciona o primeiro paciente da lista se não houver um selecionado
  // ou se o selecionado anteriormente não pertencer à lista da agenda atual
  useEffect(() => {
    if (agendas.length === 0) return;

    const existeNaLista = agendaSelecionada
      ? agendas.some((a) => String(a.CODAGENDA) === String(agendaSelecionada.CODAGENDA))
      : false;

    if (!agendaSelecionada || !existeNaLista) {
      onSelectAgenda?.(agendas[0]);
    }
  }, [agendas, agendaSelecionada, onSelectAgenda]);

  const codAgendaSelecionada = agendaSelecionada?.CODAGENDA
    ? String(agendaSelecionada?.CODAGENDA)
    : null;

  const currentCallState = codAgendaSelecionada ? callStates[codAgendaSelecionada] || null : null;

  const handleIrParaAtividades = () => {
    if (!agendaSelecionada) return;

    const params = new URLSearchParams({
      codpac: agendaSelecionada?.CODPAC,
      nomePaciente: agendaSelecionada?.NOME || '',
      ...(codFunc ? { codFunc } : {}),
      ...(nomeFunc ? { nomeFunc } : {}),
    });

    history.push(`/atividades?${params.toString()}`, { nome: agendaSelecionada?.NOME });
  };

  return (
    <>
      <style>
        {`
    /* Remove a setinha padrão (caret) do DropdownButton no Bootstrap */
    .btn-no-caret::after {
      display: none !important;
    }

    /* Estilo Ultra-Compacto para Tabela de Agendamento */
    .compact-table {
      font-size: 0.72rem !important;
    }
    .compact-table th,
    .compact-table td {
      padding: 0.15rem 0.2rem !important;
      line-height: 1.1;
    }
    .custom-status-table tbody tr td {
      background-color: transparent !important;
      color: inherit !important;
      text-decoration: inherit !important;
    }
    .custom-status-table tbody tr:hover td {
      background-color: rgba(0, 0, 0, 0.06) !important;
      color: inherit !important;
    }
  `}
      </style>

      {/* Card da Agenda Selecionada (Sempre Visível) */}
      <div className="d-none d-md-block">
        <Card className="shadow-sm mb-2 border-0 bg-light">
          {!agendaSelecionada ? (
            <div className="text-muted p-1 text-center" style={{ fontSize: '0.75rem' }}>
              <i className="fas fa-info-circle me-1" />
              Selecione um paciente na agenda para acessar as ações.
            </div>
          ) : (
            <Card.Body className="p-2">
              <Row className="align-items-center g-2">
                <Col lg={6}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="fw-bold" style={{ fontSize: '1.1rem' }}>
                      <FaUser className="me-1 text-primary" />
                      <span className="align-middle">{agendaSelecionada?.NOME}</span>
                    </div>
                    <small className="fw-bold text-secondary" style={{ fontSize: '0.85rem' }}>
                      {agendaSelecionada?.CELULARPAC}
                    </small>
                  </div>
                  <div className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                    Cód: {agendaSelecionada?.CODPAC} • {agendaSelecionada?.PROCEDIMENTO} •{' '}
                    {agendaSelecionada?.CONVENIO}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-1 w-100">
                    <div className="d-flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="success"
                        className="rounded-pill py-1 px-2"
                        style={{ fontSize: '0.9rem' }}
                        onClick={() =>
                          history.push('/pacientes', { codPac: agendaSelecionada?.CODPAC })
                        }
                      >
                        <FaUserEdit className="me-1" /> Paciente
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        className="rounded-pill py-1 px-2"
                        style={{ fontSize: '0.9rem' }}
                        onClick={handleIrParaAtividades}
                      >
                        <IoDocumentTextSharp className="me-1" /> Atividades
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        className="rounded-pill py-1 px-2"
                        style={{ fontSize: '0.9rem' }}
                        onClick={() =>
                          navigateToProntuario(agendaSelecionada?.CODPAC, {
                            agendaSelecionada,
                          })
                        }
                      >
                        <FaFileMedical className="me-1" /> Prontuário
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="warning"
                      className="rounded-pill py-1 px-2 ms-auto"
                      style={{ fontSize: '0.9rem' }}
                      onClick={() => setShowFaturamentoModal(true)}
                    >
                      <FaReceipt className="me-1" /> Faturamento
                    </Button>
                  </div>
                </Col>

                <Col lg={2} className="py-0 border-start border-end">
                  <div
                    className="d-flex flex-column justify-content-center h-100 text-nowrap px-1"
                    style={{ fontSize: '0.7rem' }}
                  >
                    <div>
                      <strong>Entrada:</strong> {agendaSelecionada?.DATA_ENTRADA_FORMATADA ?? '-'}
                    </div>
                    <div>
                      <strong>Saída:</strong> {agendaSelecionada?.DATA_SAIDA_FORMATADA ?? '-'}
                    </div>
                  </div>
                </Col>

                <Col lg={4} className="p-0">
                  <PainelDoMedico
                    agenda={agendaSelecionada}
                    callState={currentCallState}
                    onCall={onCallPatient}
                    onRecall={onRecallPatient}
                    onCancel={onCancelPatient}
                    onArrive={onArrivePatient}
                    onDone={onDonePatient}
                    onViewHistory={onViewCallHistory}
                    isProntuario={false}
                  />
                </Col>
              </Row>
            </Card.Body>
          )}
        </Card>
      </div>

      {/* Tabela de Agendamentos (Ultra Compacta) */}
      <Table
        bordered
        size="sm"
        responsive={isMobile}
        className="custom-status-table compact-table align-middle m-0"
      >
        <thead className="table-primary text-center align-middle">
          <tr>
            <th style={{ width: '45px' }}>Hora</th>
            <th>Paciente</th>
            <th>Procedimento</th>
            <th style={{ width: '35px' }}>ECG</th>
            <th>Convênio</th>
            {mostrarAtvStatus && <th>Atv</th>}
            {mostrarAtvStatus && <th>Status</th>}
            <th>Confirma</th>
            <th>Obs</th>
            <th style={{ width: '40px' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {agendas.length === 0 ? (
            <tr>
              <td colSpan={colunasVisiveis} className="text-center text-muted py-2">
                Nenhum agendamento encontrado.
              </td>
            </tr>
          ) : (
            agendas.map((a, index) => {
              const key = String(a.CODAGENDA);
              return (
                <AgendaRow
                  key={key}
                  index={index}
                  agenda={a}
                  isSelecionada={codAgendaSelecionada === key}
                  onSelectAgenda={onSelectAgenda}
                  onOpenAgenda={onOpenAgenda}
                  onRowKeyDown={handleRowKeyDown}
                  setRowRef={setRowRef}
                  verCanceladas={verCanceladas}
                  onToggleCanceladas={onToggleCanceladas}
                  mostrarAtvStatus={mostrarAtvStatus}
                />
              );
            })
          )}
        </tbody>
      </Table>

      <FaturamentoModal
        show={showFaturamentoModal}
        onHide={() => setShowFaturamentoModal(false)}
        agenda={agendaSelecionada}
        codFunc={codFunc}
        nomeFunc={nomeFunc}
        onFaturado={atualizarSituacaoFaturado}
      />
    </>
  );
}
